import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, Button, Stack } from '@mui/material';
import { IWorkProfile } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import { StarRating } from '../EsVideoVotingModal/EsRatingStars';

interface WorkProfileHeaderProps {
    profile: IWorkProfile;
    editMode?: boolean;
    onImageChange?: (base64: string, file: File) => void;
}

const WorkProfileHeader: React.FC<WorkProfileHeaderProps> = ({ profile, editMode = false, onImageChange }) => {
    const { themeStyle } = useThemeContext();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingImage, setPendingImage] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const displayName = profile.stage_name
        ? profile.stage_name
        : profile.user?.firstName
        ? profile.user?.firstName + ''
        : profile.created_by?.firstName
        ? profile.created_by?.firstName
        : '';

    // Image source logic
    const displayImage = pendingImage || profile.feature_picture || profile.user?.profile_picture;

    // Upload/crop logic
    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setSrc(URL.createObjectURL(file));
            setModalOpen(true);
        }
    };
    const handleCropSave = (base64: string, file: File) => {
        setModalOpen(false);
        setSrc('');
        setPendingImage(base64);
        setPendingFile(file);
    };
    const handleCropCancel = () => {
        setModalOpen(false);
        setSrc('');
    };
    const handlePendingCancel = () => {
        setPendingImage(null);
        setPendingFile(null);
    };
    const handlePendingSave = () => {
        if (pendingImage && pendingFile && onImageChange) {
            onImageChange(URL.createObjectURL(pendingFile), pendingFile);
        }
        setPendingImage(null);
        setPendingFile(null);
    };

    // Responsive
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
    const imageHeight = isMobile ? 140 : 200;
    const rating = Number(profile.rating).toFixed(1) ?? '--';
    const number_of_reviews = profile.number_of_reviews ?? 0;

    return (
        <Box sx={{ mt: 3 }}>
            <Box
                sx={{
                    width: '100%',
                    position: 'relative',
                    mb: 2,
                    p: 3,
                    borderRadius: 4,
                }}>
                {/* Cover Image */}
                <Box
                    sx={{
                        width: '100%',
                        height: imageHeight,
                        borderRadius: 3,
                        overflow: 'hidden',
                        backgroundColor: theme[themeStyle].secondaryBg,
                        backgroundImage: displayImage ? `url(${displayImage})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                    }}>
                    {/* Gradient Overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '35%',
                            height: {
                                xs: '70%',
                                sm: '50%',
                            },
                            background:
                                'radial-gradient(circle at bottom left, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 35%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 80%)',
                            zIndex: 1,
                        }}
                    />

                    {/* Info inside image */}
                    <Stack
                        justifyContent="flex-end"
                        sx={{
                            position: 'absolute',
                            bottom: 16,
                            left: 16,
                            zIndex: 2,
                            color: theme[themeStyle].true_white,
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].true_white,
                                fontWeight: 700,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: {
                                    xs: 16,
                                    sm: 28,
                                },
                            }}>
                            {displayName}
                        </Typography>
                        {profile.title && (
                            <Typography
                                sx={{
                                    color: theme[themeStyle].true_white,
                                    fontWeight: 700,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: {
                                        xs: 12,
                                        sm: 18,
                                    },
                                }}>
                                {profile.title}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                color: theme[themeStyle].true_white,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: {
                                    xs: 12,
                                    sm: 14,
                                },
                            }}>
                            {profile.location.address ?? ''}
                        </Typography>
                    </Stack>

                    {/* Rating Circle Overlay - now positioned at bottom right */}
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 16,
                            bottom: 16,
                            width: isMobile ? 70 : 100,
                            height: isMobile ? 70 : 100,
                            borderRadius: '50%',
                            background: theme[themeStyle].secondaryBg,
                            boxShadow: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2,
                            border: `3px solid ${theme[themeStyle].highlight}`,
                        }}>
                        <StarRating
                            defaultValue={Number(rating)}
                            ratingProps={{
                                readOnly: true,
                            }}
                            style={{
                                width: 'fit-content',
                                height: 'fit-content',
                            }}
                            iconSize={10}
                        />
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontWeight: 700,
                                fontSize: isMobile ? 16 : 20,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            {rating}
                        </Typography>
                        <Typography
                            sx={{
                                color: theme[themeStyle].grey,
                                fontSize: isMobile ? 8 : 10,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                mt: -0.5,
                            }}>
                            {number_of_reviews} reviews
                        </Typography>
                    </Box>

                    {/* Edit Icon Overlay */}
                    {editMode && !pendingImage && (
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: theme[themeStyle].primary_white,
                                zIndex: 10,
                                '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                            }}
                            onClick={handleUploadClick}
                            aria-label="Upload Profile Image">
                            <PhotoCamera sx={{ color: theme[themeStyle].true_white }} />
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </IconButton>
                    )}

                    {/* Cropper Modal */}
                    <RectangularImageCropperModal
                        open={modalOpen}
                        src={src}
                        onSave={handleCropSave}
                        onCancel={handleCropCancel}
                        aspect={16 / 5}
                        cropHeight={imageHeight}
                    />

                    {/* Pending image preview overlay with Save/Cancel */}
                    {editMode && pendingImage && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 20,
                                background: 'rgba(0,0,0,0.25)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <img
                                src={pendingImage}
                                alt="Preview"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                                }}
                            />
                            <Box sx={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 2 }}>
                                <Button variant="outlined" color="inherit" onClick={handlePendingCancel} sx={{ mr: 1 }}>
                                    Cancel
                                </Button>
                                <Button variant="contained" color="primary" onClick={handlePendingSave}>
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default WorkProfileHeader;
